/* "App Records" section source: lists records from an app's own endpoint, each as a card (see the content_source
   branch in website_hq/templates/includes/rfhq/section/grid.html). The endpoint returns
   {items: [{id, title, badge, tone, lines, actions: [{label, href | call + args, kind, confirm}], tab: {top, bottom}}]}.
   Looks come from the section's theme/surface/radius/shadow and the shared tokens (rfhq_templates.css);
   this file holds no wording and no colours. Text is set with textContent; links must be paths on this site.
   Optional, from the section: data-kept (names of choices the browser kept, passed on like the address's values) and data-refresh (ask again every so many seconds while the page is on screen). */
(function () {
	const UI = window.rfhqUI;
	if (!UI) return;
	const { h, call, localPath } = UI;
	const T = (text) => (typeof window.__ === "function" ? window.__(text) : text);  // the framework's translation of the fixed words a screen reader hears
	const TONES = ["success", "warning", "danger", "info"];

	function readArgs(el) {
		let args = {};
		try { args = JSON.parse(el.dataset.args || "{}") || {}; } catch (e) { args = {}; }
		const query = new URLSearchParams(window.location.search);
		(el.dataset.urlArgs || "").split(",").map((n) => n.trim()).filter(Boolean).forEach((name) => {
			if (query.has(name)) args[name] = query.get(name);
		});
		// A choice the visitor made on another page and the browser kept (rfhq_report_view.js keeps a town under rfhq:lookup:place); the address's own value wins.
		(el.dataset.kept || "").split(",").map((n) => n.trim()).filter(Boolean).forEach((name) => {
			if (name in args) return;
			try {
				const kept = JSON.parse(localStorage.getItem("rfhq:lookup:" + name) || "null");
				if (kept && kept.value) args[name] = kept.value;
			} catch (e) { /* nothing kept, or the browser keeps nothing: the method's own default */ }
		});
		return args;
	}

	function mount(el) {
		const body = el.querySelector(".rfhq-records-wrapper");
		const message = h("div", { class: "rfhq-records-message", role: "status" });
		el.insertBefore(message, body);
		const notice = (kind, text) => h("div", { class: "rfhq-alert rfhq-alert--" + kind, role: kind === "danger" ? "alert" : "status", text });

		// A list may come with buttons that narrow it (the endpoint says which): drawn once, above the list, by the same filter bar the product listings use.
		let barMounted = false, lastFilters = "";
		function mountBar(filters) {
			const api = window.rfhqProductCard;
			if (barMounted && filters && filters.length && JSON.stringify(filters) !== lastFilters) {  // other buttons for the choice just made
				lastFilters = JSON.stringify(filters);
				el.querySelectorAll(":scope > .rfhq-app-filters").forEach((host) => { if (host._rfhqRefresh) host._rfhqRefresh(filters); });
				return;
			}
			if (barMounted || !filters || !filters.length || !api || !api.mountAppFilters) return;
			barMounted = true;
			lastFilters = JSON.stringify(filters);
			api.mountAppFilters(el, { source: { filters, args: {} }, reset: () => load() });
		}
		// Another list on the page takes the same buttons: a choice made there narrows this one too.
		window.addEventListener("rfhq:app-filters", (event) => {
			const detail = event.detail || {};
			if (!el.isConnected || (detail.origin && el.contains(detail.origin))) return;
			const mine = (el.dataset.urlArgs || "").split(",").map((n) => n.trim()).filter(Boolean);
			if (mine.some((n) => (detail.facets || []).includes(n))) load();
		});

		window.addEventListener("rfhq:tab", () => { if (el.dataset.row && el.isConnected) arrows(); });

		// A list with nothing to show and no words for saying so takes its whole section (its heading too) away, and brings it back when there is something.
		const section = el.closest(".section") || el;
		const quiet = (on) => { section.hidden = on; };

		// A list may come with a control (the town the cards are about: rfhq_lookup.js draws it). A choice made in it asks for the list again, here, and does not move the page.
		let barEl = null, lastBar = "";
		function drawBar(spec) {
			const text = spec ? JSON.stringify(spec) : "";
			if (text === lastBar) return false;
			lastBar = text;
			const lookup = ((spec && spec.controls) || []).find((c) => c.type === "lookup");
			if (barEl) barEl.remove();
			barEl = lookup && UI.lookupControl ? h("div", { class: "rfhq-rv rfhq-record-bar" }, h("div", { class: "rv-ctl" }, UI.lookupControl(lookup, UI, { keep: false, onChoose: () => load() }))) : null;
			return true;
		}
		const inPopover = !!el.closest("[data-rfhq-popover]");  // the control then sits in the open card, under its first card
		let lastDrawn = null;
		// The place the list takes is kept while its records first load (the section's "Space Kept While Records Load"), so the page below does not jump when they arrive.
		const reserve = /^[0-9]{1,3}(\.[0-9]{1,2})?(vh|rem|px)$/.test(el.dataset.reserve || "") && !document.body.hasAttribute("data-no-perf") ? el.dataset.reserve : "";  // (data-no-perf: the performance layer's opt-out)
		async function load(flash) {
			if (reserve && !el.dataset.drawn) el.style.minHeight = reserve;
			try { await draw(flash); } finally { el.dataset.drawn = "1"; el.style.minHeight = ""; }  // (data-drawn: rfhq_templates.css keeps an optional list out of the page until here)
		}
		async function draw(flash) {
			// With a card named on the section the app only answers the card's field values and websitehq draws them (website_hq/feed.py).
			const result = el.dataset.card
				? await call("raftor_websitehq.website_hq.feed.cards", { component: el.dataset.card, source: el.dataset.source, args: JSON.stringify(readArgs(el)) })
				: await call(el.dataset.source, readArgs(el));
			message.replaceChildren(...(flash ? [notice("success", flash)] : []));
			if (!result.ok) { body.replaceChildren(notice("danger", result.message || "")); return; }
			if (el.dataset.card) {
				mountBar(result.data && result.data.filters);
				const drawn = (result.data && result.data.html) || [];
				quiet(!drawn.length && !el.dataset.empty);
				if (!drawn.length) { body.replaceChildren(...(el.dataset.empty ? [h("p", { class: "rfhq-records-empty", text: el.dataset.empty })] : [])); return; }
				const headings = (result.data && result.data.groups) || [];  // a heading where the group changes (text only)
				const cells = [];
				drawn.forEach((html, i) => {
					if (headings[i] && headings[i] !== headings[i - 1] && !el.dataset.row) cells.push(h("h3", { class: "rfhq-record-group", text: headings[i] }));
					const cell = h("div", { class: "rfhq-record-cell" }); cell.innerHTML = html;  // markup from a Web Template, which escapes what it is given
					cells.push(cell);
				});
				const barChanged = drawBar(result.data && result.data.toolbar);
				if (barChanged && barEl && !inPopover) el.insertBefore(barEl, message);
				if (barEl && inPopover) cells.splice(Math.min(1, cells.length), 0, barEl);
				const same = !barChanged && drawn.join("\u0001") === lastDrawn;  // a list asked for again that has not changed is left alone (a focus inside it stays)
				lastDrawn = drawn.join("\u0001");
				if (!same) body.replaceChildren(...cells);
				if (el.dataset.row && !same) { body.scrollTo({ left: 0 }); arrows(); autoplay(); }
				if (!same) el.dispatchEvent(new CustomEvent("rfhq:records-drawn", { bubbles: true }));  // a script that arranges the cards (rfhq_popover.js) takes them from here
				return;
			}
			const items = (result.data && result.data.items) || [];
			quiet(!items.length && !el.dataset.empty);
			if (!items.length) { body.replaceChildren(...(el.dataset.empty ? [h("p", { class: "rfhq-records-empty", text: el.dataset.empty })] : [])); return; }
			// Several groups of choices (days, each with its times): a strip to pick the group and only that group's choices below it.
			body.replaceChildren(...(items.length > 1 && items.every((i) => i.choices && (i.actions || []).length) ? groups(items) : items.flatMap((item, i) => [  // (an item may say which group it is in: a heading is put where the group changes)
				...(item.group && item.group !== (items[i - 1] || {}).group ? [h("h3", { class: "rfhq-record-group", text: item.group })] : []), record(item)])));
		}

		// A row that slides sideways: buttons to go back and forward, shown only when there is more than fits.
		function arrows() {
			let nav = el.querySelector(":scope > .rfhq-row-nav");
			const more = body.scrollWidth > body.clientWidth + 4;
			if (!more) { if (nav) nav.hidden = true; return; }
			if (!nav) {
				const go = (dir) => body.scrollBy({ left: dir * Math.max(240, body.clientWidth * 0.8), behavior: "smooth" });
				nav = h("div", { class: "rfhq-row-nav" },
					h("button", { type: "button", class: "rfhq-row-btn", "aria-label": T("Previous"), text: "\u2039", onclick: () => go(-1) }),
					h("button", { type: "button", class: "rfhq-row-btn", "aria-label": T("More"), text: "\u203a", onclick: () => go(1) }));
				el.insertBefore(nav, body);
			}
			nav.hidden = false;
		}

		// A row that moves on by itself (the section asks for it, with the seconds between slides): one card at a time, back to the start after the last. It stops for good when the visitor
		// touches, scrolls or tabs into it, waits while the pointer is over it or the row is off the screen, and never starts for anyone who asked for less motion.
		let moving = null;
		function autoplay() {
			const seconds = parseInt(el.dataset.autoplay, 10);
			if (!seconds || moving !== null || matchMedia("(prefers-reduced-motion: reduce)").matches || el.closest("[data-no-anim]") || document.body.hasAttribute("data-no-anim")) return;  // (the animation engine's own opt-outs)
			let over = false, seen = true;
			const stop = () => { clearInterval(moving); moving = false; };
			["pointerdown", "touchstart", "wheel", "focusin", "keydown"].forEach((name) => el.addEventListener(name, stop, { once: true, passive: true }));
			el.addEventListener("mouseenter", () => { over = true; });
			el.addEventListener("mouseleave", () => { over = false; });
			if (window.IntersectionObserver) new IntersectionObserver((entries) => { seen = entries[entries.length - 1].isIntersecting; }).observe(el);
			moving = setInterval(() => {
				const cells = body.children;
				if (over || !seen || document.hidden || cells.length < 2 || body.scrollWidth <= body.clientWidth + 4) return;
				const at = body.scrollLeft, next = Array.from(cells).find((cell) => cell.offsetLeft - body.offsetLeft > at + 4);
				const last = body.scrollLeft + body.clientWidth >= body.scrollWidth - 4;
				body.scrollTo({ left: last || !next ? 0 : next.offsetLeft - body.offsetLeft, behavior: "smooth" });
			}, Math.max(2, seconds) * 1000);
		}

		function groups(items) {
			const strip = h("div", { class: "rfhq-days", role: "tablist" });
			const choices = h("div", { class: "rfhq-day-times", role: "tabpanel" });
			const pick = (n) => {
				[...strip.children].forEach((tab, i) => { tab.classList.toggle("active", i === n); tab.setAttribute("aria-selected", String(i === n)); });
				choices.replaceChildren(...items[n].actions.map((a) => control(a, choices, false)));
			};
			items.forEach((item, n) => {
				const tab = item.tab || { top: "", bottom: item.title || "" };  // the endpoint words the tab; nothing here reads a date
				strip.append(h("button", { type: "button", class: "rfhq-day", role: "tab", onclick: () => pick(n) },
					h("span", { class: "rfhq-day-name", text: tab.top || "" }), h("span", { class: "rfhq-day-date", text: tab.bottom || "" })));
			});
			pick(0);
			return [strip, choices];
		}

		function record(item) {
			const actions = h("div", { class: "rfhq-record-actions" });
			const agendaLayout = item.layout === "agenda";
			const linksOnly = item.layout === "links";  // a bare line of quiet links (no card, no title), such as "Mark all as read" over a list
			const mainBox = agendaLayout ? h("div", { class: "rfhq-record-main" }) : null;  // (in an agenda line the main button stands beside the time, the others are quiet links under the words)
			// The first action that is not destructive is the main one, listed first; destructive ones go last.
			const main = item.choices || linksOnly ? -1 : (item.actions || []).findIndex((a) => a.kind !== "danger");
			const rank = (a, i) => (i === main ? 0 : a.kind === "danger" ? 2 : 1);
			(item.actions || []).map((a, i) => [a, i]).sort((x, y) => rank(x[0], x[1]) - rank(y[0], y[1]) || x[1] - y[1])
				.forEach(([action, i]) => (mainBox && i === main ? mainBox : actions).append(control(action, actions, i === main, agendaLayout || linksOnly)));
			const tone = TONES.includes(item.tone) ? " rfhq-badge--" + item.tone : "";
			// Optional extras, all from the endpoint: a photo (a path on this site) or else initials, tags, and a rating out of 5.
			const photo = item.image && localPath(item.image);
			const initials = (typeof item.avatar === "string" ? item.avatar : item.title || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
			const avatar = photo ? h("img", { class: "rfhq-record-avatar", src: photo, alt: "", loading: "lazy" })
				: item.avatar ? h("span", { class: "rfhq-record-avatar rfhq-record-avatar--initials", "aria-hidden": "true", text: initials }) : null;
			const rating = Number(item.rating) > 0 ? h("span", { class: "rfhq-record-rating", "aria-label": item.rating_label || String(item.rating), text: Number(item.rating).toFixed(1) + (item.rating_count ? " (" + item.rating_count + ")" : "") }) : null;
			const tags = (item.tags || []).length ? h("ul", { class: "rfhq-record-tags" }, ...item.tags.map((t) => h("li", { text: t }))) : null;
			const head = h("header", { class: "rfhq-record-head" }, avatar,
				h("div", { class: "rfhq-record-headtext" }, h("strong", { class: "rfhq-record-title", text: item.title || "" }), rating),
				item.badge ? h("span", { class: "rfhq-record-badge" + tone, text: item.badge }) : null);
			// A line of someone's agenda (`layout: "agenda"`): compact, the main button first and the other buttons quiet beside it, a bar down the side in the tone of its state.
			const agenda = (item.layout === "agenda" ? " rfhq-record--agenda" + (TONES.includes(item.tone) ? " rfhq-record--" + item.tone : "") : "") + (linksOnly ? " rfhq-record--links" : "");
			return h("article", { class: "rfhq-card rfhq-record" + agenda }, head, tags,
				...(item.lines || []).map((line) => h("p", { class: "rfhq-record-line", text: line })),
				...(mainBox && mainBox.childNodes.length ? [mainBox] : []),
				// more than one other action in an agenda line: they wait behind a "More" (the words come with the item), so the line stays short
				agendaLayout && actions.childNodes.length > 1 && item.more_label ? h("details", { class: "rfhq-record-more" }, h("summary", { text: item.more_label }), actions) : actions);
		}

		function control(action, holder, isMain, quiet) {
			const cls = "rfhq-record-action " + (action.kind === "danger" ? "rfhq-record-action--danger" : isMain ? "rfhq-cta-primary" : quiet ? "" : "rfhq-cta-secondary") + (quiet && !isMain ? " rfhq-record-action--link" : "");
			if (action.href) return h("a", { class: cls, href: localPath(action.href) || "#", text: action.label });
			if (action.window) return h("button", { type: "button", class: cls, text: action.label, onclick: (event) => openWindow(action, event.currentTarget) });
			return h("button", { type: "button", class: cls, text: action.label, onclick: () => {
				if (!action.confirm) return run(action);
				const ask = h("span", { class: "rfhq-record-confirm", role: "alert" }, h("span", { text: action.confirm }),
					h("button", { type: "button", class: "rfhq-record-action rfhq-record-action--danger", text: action.confirm_label || action.label, onclick: () => run(action) }),
					h("button", { type: "button", class: "rfhq-record-action rfhq-cta-secondary", text: action.cancel_label || "×", onclick: () => ask.remove() }));
				holder.append(ask);
			} });
		}

		// An action that opens a provider's window (a payment window, say), described as data by the app and run here, so that the page has no script of its own: the provider's script is loaded only
		// from a host the apps declared (data-script-hosts), the window is opened with the options the app gave and the provider's answer goes to the method the app named, after which the person
		// is sent on. {scripts, open: {global, options, handler}, done: {call, args, proof_arg}, failed} for a window; {ask, proof, done} for a provider with none (a yes or no).
		const HOSTS = (el.dataset.scriptHosts || "").split(",").map((x) => x.trim().toLowerCase()).filter(Boolean);
		const scripts = {};
		function loadScript(address) {
			let url;
			try { url = new URL(address, window.location.href); } catch (e) { return Promise.reject(new Error("bad address")); }
			if (url.protocol !== "https:" || !HOSTS.includes(url.hostname.toLowerCase())) return Promise.reject(new Error("not allowed"));
			if (!scripts[url.href]) scripts[url.href] = new Promise((resolve, reject) => {
				const tag = document.createElement("script");
				tag.src = url.href; tag.async = true; tag.onload = resolve;
				tag.onerror = () => { delete scripts[url.href]; reject(new Error("not loaded")); };
				document.head.append(tag);
			});
			return scripts[url.href];
		}
		async function openWindow(action, button) {
			const w = action.window || {}, then = w.done || {};
			const done = async (proof) => {
				const result = await call(then.call, { ...(then.args || {}), [then.proof_arg || "proof"]: JSON.stringify(proof || {}) });
				if (!result.ok) { message.replaceChildren(notice("danger", result.message || "")); return; }
				const go = result.data && localPath(result.data.redirect || result.data.redirect_to);
				if (go) { window.location.href = go; return; }
				load(result.data && result.data.message);
			};
			if (w.ask) { if (window.confirm(w.ask)) await done(w.proof); return; }
			button.disabled = true;
			try {
				await Promise.all((w.scripts || []).map(loadScript));
				const open = w.open || {};
				const Provider = /^[A-Za-z_$][\w$]*$/.test(open.global || "") ? window[open.global] : null;
				if (typeof Provider !== "function") throw new Error("no window");
				new Provider({ ...(open.options || {}), [open.handler || "handler"]: done }).open();
			} catch (e) {
				message.replaceChildren(notice("danger", w.failed || ""));
			} finally { button.disabled = false; }
		}

		async function run(action) {
			const result = await call(action.call, action.args || {});
			if (!result.ok) { message.replaceChildren(notice("danger", result.message || "")); return; }
			const go = result.data && localPath(result.data.redirect);  // the app may send the person on (for example to the cart)
			if (go) { window.location.href = go; return; }
			load(result.data && result.data.message);
			el.dispatchEvent(new CustomEvent("rfhq:records-changed", { bubbles: true }));  // (a list inside an accordion line tells the line's list, which refreshes what the line says)
		}
		el.addEventListener("rfhq:reload", (event) => { if (event.target === el) load(); });

		// Buttons set on the section (for example "Add an offering" above the list, "Back" below it).
		let links = [];
		try { links = JSON.parse(el.dataset.links || "[]") || []; } catch (e) { links = []; }
		// A link to the page the visitor is already on (same path and same query) is shown as the chosen one, so a row of links works as a filter.
		const here = (href) => {
			try {
				const to = new URL(href, window.location.origin), now = new URL(window.location.href);
				const keys = (u) => [...u.searchParams.keys()].sort().map((k) => k + "=" + u.searchParams.get(k)).join("&");
				return to.pathname === now.pathname && keys(to) === keys(now);
			} catch (e) { return false; }
		};
		const row = (where) => {
			const mine = links.filter((l) => (l.where || "bottom") === where && localPath(l.href));
			return mine.length ? h("div", { class: "rfhq-records-links rfhq-records-links--" + where },
				...mine.map((l) => { const on = l.kind === "primary" || here(localPath(l.href));
					return h("a", { class: "rfhq-record-action " + (on ? "rfhq-cta-primary" : "rfhq-cta-secondary"), href: localPath(l.href), text: l.label, "aria-current": here(localPath(l.href)) ? "page" : null }); })) : null;
		};
		const top = row("top"), bottom = row("bottom");
		if (top) el.insertBefore(top, message);
		if (bottom) el.append(bottom);

		body.replaceChildren(h("div", { class: "rfhq-records-skeleton", "aria-hidden": "true" }));
		load();
		// Asked again every so often while the page is on screen (not behind another tab), and at once when it comes back after longer than that.
		const every = Math.max(0, Number(el.dataset.refresh) || 0);
		if (every >= 15 && el.dataset.card) {
			let asked = Date.now();
			const again = () => { if (el.isConnected && document.visibilityState === "visible") { asked = Date.now(); load(); } };
			window.setInterval(again, every * 1000);
			document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible" && Date.now() - asked > every * 1000) again(); });
		}
	}

	window.rfhqMountRecords = () => document.querySelectorAll(".rfhq-app-records:not([data-mounted])").forEach((el) => { el.setAttribute("data-mounted", "1"); mount(el); });  // for a list drawn after the page (an accordion line opened)
	function init() { document.querySelectorAll(".rfhq-app-records:not([data-mounted])").forEach((el) => { el.setAttribute("data-mounted", "1"); mount(el); }); }
	if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init); else init();
})();
